import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchContentList, toggleActivate, IContent, IFetchContentListParams } from 'api/koach/v2/content.api';
import { IPagination } from 'api/interfaces/common.interface';
import { getCurrentLanguageCode } from 'helpers/String.helper';
import VideoPlayer from 'commons/video/VideoPlayer';
import { usePopup } from 'contexts/PopupContext';
import ContentCommentPopup from '../popup/ContentCommentPopup';

const KoachCoachingContents = () => {
    const navigate = useNavigate();
    const { showPopup } = usePopup();
    const [contents, setContents] = useState<IContent[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [filter, setFilter] = useState<'all' | boolean>('all');
    const [showReactionTooltip, setShowReactionTooltip] = useState<number | null>(null);
    const [togglingContentIds, setTogglingContentIds] = useState<Set<number>>(new Set());
    const [pagination, setPagination] = useState<IPagination>({
        currentPage: 1,
        totalPages: 1,
        totalCount: 0,
        hasNextPage: false,
        hasPrevPage: false
    });

    const loadContents = useCallback(async (page: number = 1, isActivated?: boolean, signal?: AbortSignal) => {
        try {
            setLoading(true);
            setError(null);
            // 로딩 시작 시 목록을 먼저 비워서 스켈레톤이 보이도록 함
            setContents([]);
            
            const params: IFetchContentListParams = {
                page,
                limit: 20,
                ...(isActivated !== undefined && { isActivated })
            };

            const response = await fetchContentList(params, signal);
            
            // 요청이 취소되었는지 확인
            if (signal?.aborted) {
                return;
            }
            
            setContents(response.contentList);
            // API 응답의 pagination을 IPagination 형식으로 변환
            const apiPagination = response.pagination as any;
            setPagination({
                currentPage: apiPagination.currentPage || 1,
                totalPages: apiPagination.totalPages || 1,
                totalCount: apiPagination.totalItems || 0,
                hasNextPage: (apiPagination.currentPage || 1) < (apiPagination.totalPages || 1),
                hasPrevPage: (apiPagination.currentPage || 1) > 1
            });
        } catch (err: any) {
            // 요청 취소 에러는 무시
            if (err.name === 'AbortError' || err.name === 'CanceledError' || signal?.aborted) {
                return;
            }
            // 요청이 취소되었는지 다시 확인
            if (signal?.aborted) {
                return;
            }
            console.error('컨텐츠 목록 조회 실패:', err);
            setError(err.message || '컨텐츠를 불러오는데 실패했습니다.');
            setContents([]);
        } finally {
            // 요청이 취소되지 않았을 때만 로딩 상태 해제
            if (!signal?.aborted) {
                setLoading(false);
            }
        }
    }, []);

    useEffect(() => {
        const abortController = new AbortController();
        const isActivated = filter === 'all' ? undefined : filter;
        loadContents(1, isActivated, abortController.signal);
        
        // cleanup 함수에서 이전 요청 취소
        return () => {
            abortController.abort();
        };
    }, [filter, loadContents]);

    const handleContentClick = (contentId: number) => {
        navigate(`/koach/coaching/contents/${contentId}`);
    };

    const handleCreateClick = () => {
        navigate('/koach/coaching/contents/create');
    };

    const handleEditClick = (e: React.MouseEvent, contentId: number) => {
        e.stopPropagation();
        navigate(`/koach/coaching/contents/${contentId}/edit`);
    };

    const handleStatusToggle = async (e: React.MouseEvent, contentId: number, currentStatus: boolean) => {
        e.stopPropagation();
        try {
            // 로딩 시작
            setTogglingContentIds(prev => new Set(prev).add(contentId));
            
            const newStatus = !currentStatus;
            const response = await toggleActivate(contentId, newStatus);
            console.log('토글 응답:', response);
            
            // 목록 업데이트 - 응답의 isActivated 사용, 없으면 전달한 값 사용
            const updatedStatus = response?.isActivated !== undefined ? response.isActivated : newStatus;
            
            // 현재 필터와 상태가 맞지 않으면 목록에서 제거
            const shouldRemove = filter !== 'all' && 
                ((filter === true && updatedStatus === false) || (filter === false && updatedStatus === true));
            
            setContents(prevContents => {
                if (shouldRemove) {
                    // 필터 조건과 맞지 않으면 목록에서 제거
                    return prevContents.filter(content => content.id !== contentId);
                } else {
                    // 필터 조건과 맞으면 상태만 업데이트
                    return prevContents.map(content => 
                        content.id === contentId 
                            ? { ...content, isActivated: updatedStatus }
                            : content
                    );
                }
            });
        } catch (error: any) {
            console.error('컨텐츠 상태 업데이트 실패:', error);
            alert(error.message || '컨텐츠 상태 업데이트에 실패했습니다.');
        } finally {
            // 로딩 종료
            setTogglingContentIds(prev => {
                const newSet = new Set(prev);
                newSet.delete(contentId);
                return newSet;
            });
        }
    };

    const getTitle = (content: IContent): string => {
        // 기본 제목 사용, 없으면 번역에서 가져오기
        if (content.title) {
            return content.title;
        }
        if (content.multiTexts && content.multiTexts.length > 0) {
            const currentLang = getCurrentLanguageCode();
            const currentMultiText = content.multiTexts.find(mt => mt.languageCode === currentLang);
            return currentMultiText?.title || content.multiTexts[0]?.title || '제목 없음';
        }
        return '제목 없음';
    };


    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('ko-KR', { year: 'numeric', month: 'long', day: 'numeric' });
    };

    const getDesc = (content: IContent): string => {
        if (content.desc) {
            return content.desc;
        }
        if (content.multiTexts && content.multiTexts.length > 0) {
            return content.multiTexts[0]?.desc || '';
        }
        return '';
    };

    const handleCommentClick = (e: React.MouseEvent, content: IContent) => {
        e.stopPropagation();
        const popupContent = (
            <ContentCommentPopup
                contentId={content.id}
                contentTitle={getTitle(content)}
                contentDesc={getDesc(content)}
                onClose={() => showPopup(null)}
                onRefresh={() => {
                    const isActivated = filter === 'all' ? undefined : filter;
                    loadContents(pagination.currentPage, isActivated);
                }}
            />
        );
        showPopup(popupContent);
    };

    // 리액션 버튼 클릭 핸들러
    const handleReactionClick = (contentId: number, event: React.MouseEvent) => {
        event.stopPropagation();
        setShowReactionTooltip(showReactionTooltip === contentId ? null : contentId);
    };

    // 외부 클릭 시 툴팁 닫기
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (showReactionTooltip !== null) {
                setShowReactionTooltip(null);
            }
        };

        if (showReactionTooltip !== null) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showReactionTooltip]);

    // 콘텐츠 카드 스켈레톤 컴포넌트
    const ContentCardSkeleton = () => (
        <div className="bg-white rounded-lg shadow-sm overflow-hidden animate-pulse">
            {/* 비디오 영역 스켈레톤 */}
            <div className="w-full h-48 bg-gray-200"></div>
            
            <div className="p-4">
                {/* 제목 스켈레톤 */}
                <div className="h-6 bg-gray-200 rounded w-3/4 mb-2"></div>
                
                {/* 설명 스켈레톤 */}
                <div className="space-y-2 mb-4">
                    <div className="h-4 bg-gray-200 rounded w-full"></div>
                    <div className="h-4 bg-gray-200 rounded w-2/3"></div>
                </div>
                
                {/* 리액션/댓글/즐겨찾기 카운트 스켈레톤 */}
                <div className="flex items-center gap-4 mb-3">
                    <div className="h-4 bg-gray-200 rounded w-12"></div>
                    <div className="h-4 bg-gray-200 rounded w-12"></div>
                    <div className="h-4 bg-gray-200 rounded w-12"></div>
                </div>
                
                {/* 날짜와 버튼 스켈레톤 */}
                <div className="flex items-center justify-between">
                    <div className="h-3 bg-gray-200 rounded w-24"></div>
                    <div className="flex gap-2">
                        <div className="h-7 bg-gray-200 rounded w-16"></div>
                        <div className="h-7 bg-gray-200 rounded w-12"></div>
                    </div>
                </div>
            </div>
        </div>
    );

    return (
        <div className="min-h-screen bg-gray-50 pt-20 pb-24 px-4">
            <div className="max-w-6xl mx-auto">
                <div className="flex justify-between items-center mb-6">
                    <h1 className="text-2xl font-bold text-gray-900">콘텐츠</h1>
                    <button 
                        onClick={handleCreateClick}
                        className="px-4 py-2 bg-[#CF3688] text-white rounded-lg hover:bg-[#B52E74] transition-colors">
                        새 콘텐츠 작성
                    </button>
                </div>

                <div className="flex gap-2 mb-6">
                    <button
                        onClick={() => setFilter('all')}
                        className={`px-4 py-2 rounded-lg transition-colors ${
                            filter === 'all' 
                                ? 'bg-[#CF3688] text-white' 
                                : 'bg-white text-gray-700 hover:bg-gray-100'
                        }`}
                    >
                        전체
                    </button>
                    <button
                        onClick={() => setFilter(true)}
                        className={`px-4 py-2 rounded-lg transition-colors ${
                            filter === true 
                                ? 'bg-[#CF3688] text-white' 
                                : 'bg-white text-gray-700 hover:bg-gray-100'
                        }`}
                    >
                        공개
                    </button>
                    <button
                        onClick={() => setFilter(false)}
                        className={`px-4 py-2 rounded-lg transition-colors ${
                            filter === false 
                                ? 'bg-[#CF3688] text-white' 
                                : 'bg-white text-gray-700 hover:bg-gray-100'
                        }`}
                    >
                        비공개
                    </button>
                </div>
                
                {loading ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {Array.from({ length: 6 }).map((_, index) => (
                            <ContentCardSkeleton key={`skeleton-${index}`} />
                        ))}
                    </div>
                ) : contents.length === 0 ? (
                    <div className="bg-white rounded-lg shadow-sm p-8 text-center">
                        {filter === 'all' ? (
                            <>
                                <p className="text-gray-500">등록된 컨텐츠가 없습니다.</p>
                                <button
                                    onClick={handleCreateClick}
                                    className="mt-4 px-4 py-2 bg-[#CF3688] text-white rounded-lg hover:bg-[#B52E74] transition-colors"
                                >
                                    첫 컨텐츠 작성하기
                                </button>
                            </>
                        ) : filter === true ? (
                            <p className="text-gray-500">현재 공개중인 컨텐츠가 없습니다.</p>
                        ) : (
                            <p className="text-gray-500">현재 비공개중인 컨텐츠가 없습니다.</p>
                        )}
                    </div>
                ) : (
                    <>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {contents.map((content) => (
                                <div 
                                    key={content.id} 
                                    className="bg-white rounded-lg shadow-sm overflow-hidden hover:shadow-md transition-shadow"
                                >
                                    <div className="relative">
                                        {!content.isActivated && (
                                            <div className="absolute top-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded z-10">
                                                비공개
                                            </div>
                                        )}
                                        {content.video ? (
                                            <div>
                                                <VideoPlayer
                                                    video={content.video}
                                                    className="w-full h-48"
                                                    enableVideoLog={false}
                                                />
                                            </div>
                                        ) : (
                                            <div 
                                                className="w-full h-48 bg-gray-200 flex items-center justify-center cursor-pointer"
                                                onClick={() => handleContentClick(content.id)}
                                            >
                                                <span className="text-gray-400">비디오 없음</span>
                                            </div>
                                        )}
                                    </div>
                                    
                                    <div className="p-4">
                                        <h3 className="text-lg font-semibold text-gray-900 mb-2 line-clamp-2">
                                            {getTitle(content)}
                                        </h3>
                                        
                                        {(content.desc || (content.multiTexts && content.multiTexts.length > 0)) && (
                                            <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                                                {content.desc || (content.multiTexts?.[0]?.desc) || ''}
                                            </p>
                                        )}
                                        
                                        {/* 리액션, 댓글, 즐겨찾기 카운트 */}
                                        <div className="flex items-center gap-4 mb-3 text-sm text-gray-600">
                                            <div className="relative">
                                                <button
                                                    onClick={(e) => handleReactionClick(content.id, e)}
                                                    className="flex items-center gap-1 hover:opacity-70 transition-opacity"
                                                >
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                    </svg>
                                                    <span>{content.reactionCount || 0}</span>
                                                </button>
                                                
                                                {/* 리액션 선택 툴팁 */}
                                                {showReactionTooltip === content.id && (
                                                    <div 
                                                        className="absolute bottom-full left-0 mb-2 flex items-center gap-1 bg-white border border-gray-200 rounded-lg shadow-lg p-1 z-10 whitespace-nowrap"
                                                        onClick={(e) => e.stopPropagation()}
                                                    >
                                                        <button
                                                            className="flex items-center gap-1 px-2 py-1 rounded transition-colors hover:bg-gray-100 min-w-0"
                                                            title="큰 도움이 됐어요"
                                                        >
                                                            <img 
                                                                src="/imgs/icons/icon-reaction-very-helpful.svg"
                                                                alt="큰 도움이 됐어요"
                                                                className="w-6 h-6 flex-shrink-0"
                                                            />
                                                            <span className="text-xs text-gray-600">
                                                                {content.reactionCounts?.veryHelpful || 0}
                                                            </span>
                                                        </button>
                                                        <button
                                                            className="flex items-center gap-1 px-2 py-1 rounded transition-colors hover:bg-gray-100 min-w-0"
                                                            title="도움이 됐어요"
                                                        >
                                                            <img 
                                                                src="/imgs/icons/icon-reaction-helpful.svg"
                                                                alt="도움이 됐어요"
                                                                className="w-6 h-6 flex-shrink-0"
                                                            />
                                                            <span className="text-xs text-gray-600">
                                                                {content.reactionCounts?.helpful || 0}
                                                            </span>
                                                        </button>
                                                        <button
                                                            className="flex items-center gap-1 px-2 py-1 rounded transition-colors hover:bg-gray-100 min-w-0"
                                                            title="아쉬웠어요"
                                                        >
                                                            <img 
                                                                src="/imgs/icons/icon-reaction-disappointed.svg"
                                                                alt="아쉬웠어요"
                                                                className="w-6 h-6 flex-shrink-0"
                                                            />
                                                            <span className="text-xs text-gray-600">
                                                                {content.reactionCounts?.disappointed || 0}
                                                            </span>
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                            <div 
                                                className="flex items-center gap-1 cursor-pointer hover:opacity-70 transition-opacity"
                                                onClick={(e) => handleCommentClick(e, content)}
                                            >
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                                                </svg>
                                                <span>{content.commentCount || 0}</span>
                                            </div>
                                            <div className="flex items-center gap-1">
                                                <img 
                                                    src="/imgs/icons/icon-favorite.svg" 
                                                    alt="즐겨찾기" 
                                                    className="w-4 h-4"
                                                />
                                                <span>{content.favoriteCount || 0}</span>
                                            </div>
                                        </div>
                                        
                                        <div className="flex items-center justify-between">
                                            <span className="text-xs text-gray-400">{formatDate(content.createdAt)}</span>
                                            <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                                                <button
                                                    onClick={(e) => handleStatusToggle(e, content.id, content.isActivated)}
                                                    disabled={togglingContentIds.has(content.id)}
                                                    className={`px-3 py-1 rounded text-sm transition-colors flex items-center gap-2 ${
                                                        content.isActivated
                                                            ? 'bg-gray-500 text-white hover:bg-gray-600'
                                                            : 'bg-[#CF3688] text-white hover:bg-[#B52E74]'
                                                    } ${togglingContentIds.has(content.id) ? 'opacity-50 cursor-not-allowed' : ''}`}
                                                >
                                                    {togglingContentIds.has(content.id) && (
                                                        <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                        </svg>
                                                    )}
                                                    {content.isActivated ? '비공개' : '공개'}
                                                </button>
                                                <button
                                                    onClick={(e) => handleEditClick(e, content.id)}
                                                    className="px-3 py-1 bg-gray-100 text-gray-700 rounded text-sm hover:bg-gray-200 transition-colors"
                                                >
                                                    수정
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {pagination.totalPages > 1 && (
                            <div className="mt-6 flex justify-center gap-2">
                                {Array.from({ length: pagination.totalPages }, (_, i) => i + 1).map((page) => (
                                    <button
                                        key={page}
                                        onClick={() => {
                                            const isActivated = filter === 'all' ? undefined : filter;
                                            loadContents(page, isActivated);
                                        }}
                                        className={`px-4 py-2 rounded ${
                                            page === pagination.currentPage
                                                ? 'bg-[#CF3688] text-white'
                                                : 'bg-white text-gray-700 hover:bg-gray-100'
                                        }`}
                                    >
                                        {page}
                                    </button>
                                ))}
                            </div>
                        )}
                    </>
                )}
                
                {error && !loading && contents.length === 0 && (
                    <div className="bg-white rounded-lg shadow-sm p-8 text-center">
                        <div className="text-red-500">{error}</div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default KoachCoachingContents;
